import axios from 'axios';
import { authHeaderField, getAuthHeader } from 'state/request-auth';
import APIError from 'api/APIError';

const stage = 'v1';
export const apiBase = `https://api.bythebay.cool/${stage}`;

const handleError = (error) => {
  // if we got an error back from the server, throw it
  if (error && error.response && error.response.data) {
    throw new APIError({
      message: error.response.data.message,
      response: error.response
    });
  } else {
    throw new Error(error.message);
  }
};

/**
 * Begin a user registration. This triggers a user registration
 * email to the user's email (username).
 *
 * Parameters:
 * @data: {
 *   username: "me@email",
 *   first: "firstname",
 *   last: "lastname",
 * }
 */
export const register = (data) => {
  const url = `${apiBase}/users/register`;
  const body = {
    username: data.username,
    first: data.first,
    last: data.last,
  };
  return axios.post(url, body)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Complete a user registration. Token should have been received
 * by the user's registration email.
 *
 * Parameters:
 * @data: {
 *   username: "me@email",
 *   password: "set a password",
 *   token: "registration token",
 * }
 */
export const registerComplete = (data) => {
  const url = `${apiBase}/users/register/${data.token}`;
  const body = {
    username: data.username,
    password: data.password,
  };
  return axios.post(url, body)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * User login. Sets the auth token to the state on success.
 *
 * Parameters:
 * @data: {
 *   username: "me@email",
 *   password: "my password",
 * }
 */
export const login = (data) => {
  const url = `${apiBase}/login`;
  const body = {
    username: data.username,
    password: data.password,
  };
  const config = {
    method: 'post',
    url: url,
    data: body,
  };
  return axios(config)
    .then((response) => {
      return response.data;
    })
    .catch(handleError);
};

/**
 * Initiates a password reset/forgot flow. This
 * sends a reset request email.
 *
 * Parameters:
 * @data: {
 *   username: "me@email"
 * }
 */
export const resetPassword = (data) => {
  const url = `${apiBase}/users/reset-password`;
  const body = {
    username: data.username,
  };
  const config = {
    method: 'post',
    url: url,
    data: body,
  };
  return axios(config)
    .then((response) => {
      return response.data;
    })
    .catch(handleError);
};

/**
 * Complete a password reset request. Needs the token
 * from the password reset email.
 *
 * data: {
 *   username: "person@email",
 *   password: "new password",
 *   token: "token from email",
 * }
 */
export const resetPasswordComplete = (data) => {
  const token = data.token;
  const url = `${apiBase}/users/reset-password/${token}`;
  const body = {
    username: data.username,
    password: data.password,
  };
  const config = {
    method: 'post',
    url: url,
    data: body,
  };
  return axios(config)
    .then((response) => {
      return response.data;
    })
    .catch(handleError);
};

/**
 * Sends email subscriber data. Records a subscription to DB.
 * Updates the subscriber or creates them.
 *
 * Parameters:
 * @data: {
 *   email: "me@email",
 *   first: "firstname",
 *   last: "lastname",
 *   zip: "98118",
 *   roles: "creator,guru,ally,other"
 *   roleOtherText: "some other role",
 *   wantsToSubscribe: true|false
 * }
 *
 */
export const subscriber = (data) => {
  const url = `${apiBase}/subscribers`;
  const config = {
    method: 'put',
    url: url,
    data: data,
  };
  return axios(config)
    .then((response) => {
      return response.data;
    })
    .catch(handleError);
};

/**
 * Record donation data.
 *
 * Parameters:
 * @data: {
 *   name: "First Last",
 *   email: "donor@bythebay.cool",
 *   amount: 100.0,
 *   token: "stripePaymentToken",
 *   wantsToSubscribe: true|false,
 *   forWhat: "donation" (for now)
 *   paid: true|false,
 * }
 */
export const donation = (data) => {
  const url = `${apiBase}/donations`;
  const config = {
    method: 'post',
    url: url,
    data: data,
  };
  return axios(config)
    .then((response) => {
      return response.data;
    })
    .catch(handleError);
};

/**
 * Requests GET /users which returns the currently authed user (by token).
 *
 * Parameters:
 * @authData: { username, password }
 *
 * If authData isn't provided (or is null), then the token is attempted to be
 * fetched from the state store.
 */
export const getUser = (authData=null) => {
  const url = `${apiBase}/users`;
  let authHeader = null;
  if (authData && authData.username && authData.token) {
    authHeader = authHeaderField(authData.username, authData.token);
  } else {
    authHeader = getAuthHeader();
  }
  const headers = {
    Authorization: authHeader
  };
  const config = {
    method: 'get',
    url: url,
    headers: headers,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Updates the currently logged in user (POST /users). Authentication
 * data is pulled from the store.
 *
 * Parameters:
 * @userData: {
 *   first: "NewFirstName",
 *   last: "NewLastName"
 * }
 */
export const updateUser = (userData) => {
  const url = `${apiBase}/users`;
  const authHeader = getAuthHeader();
  const headers = {
    Authorization: authHeader
  };
  const config = {
    method: 'post',
    url: url,
    data: userData,
    headers: headers,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Create/update user's voter preference
 *
 * Parameters:
 * @votePrefPayload = {
 *   pageId: the candidate/prop page to write to
 *   userId: the currently logged in user
 *   data: voter preference data in the form of:
 *   {
 *     vote: "Y"|"N"|null,
 *     shared: true,
 *   }
 * }
 */
export const writeVotePref = (votePrefPayload) => {
  const { pageId, data } = votePrefPayload;
  const url = `${apiBase}/page/${pageId}/pref`;
  const authHeader = getAuthHeader();
  const headers = {
    Authorization: authHeader,
  };
  const config = {
    method: 'post',
    url: url,
    data: data,
    headers: headers,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Get your own or another user' vote pref.
 *
 * Parameters:
 * @payload.pageId: the candidate/prop page to write to
 * @payload.userId: the user's vote pref page
 */
export const getVotePref = (votePrefPayload) => {
  const {pageId, userId} = votePrefPayload
  const url = `${apiBase}/page/${pageId}/pref/${userId}`;
  // authorization is optional on this request
  const authHeader = getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  const config = {
    method: 'get',
    url: url,
    headers: headers,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Create/update user's ballot (security controls only)
 *
 * Parameters:
 * @payload.data.shared: {true|false} sharing of ballot and vote prefs
 */
export const writeBallot = (payload) => {
  const { data } = payload;
  const url = `${apiBase}/ballot`;
  const authHeader = getAuthHeader();
  const headers = {
    Authorization: authHeader,
  };
  const config = {
    method: 'post',
    url: url,
    data: data,
    headers: headers,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Get your own or another user's ballot.
 *
 * Parameters:
 * @payload.userId: the owner userId of the ballot to fetch
 */
export const getBallot = (payload) => {
  const { userId } = payload;
  const url = `${apiBase}/ballot/${userId}`;
  // authorization is optional on this request
  const authHeader = getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  const config = {
    method: 'get',
    url: url,
    headers: headers,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * List comments/replies for a vote pref page.
 *
 * GET /page/{pageId}/pref/{userId}/comments
 * GET /page/{pageId}/pref/{userId}/comments/{commentId}
 *
 * Parameters:
 * @payload.pageId: specified page id
 * @payload.userId: voter pref page user id
 * @payload.commentId: (Optional) commentId to get reply comments for
 * @payload.nextUrl: (Optional) Next pagination page (from response page.next)
 *   Note that this overrides the above two identifiers (should be same)
 * @payload.sectionId: (Optional) the section to get comments for
 */
export const listComments = (payload) => {
  const {pageId, userId, commentId, sectionId } = payload;
  // paginated responses return page.next, which indicates the next
  // full url to query to get the next page.
  let url = `${apiBase}/page/${pageId}/pref/${userId}/comments`;
  if (commentId) {
    url += `/${commentId}/replies`
  }
  if (payload.nextUrl) {
    url = `${apiBase}${payload.nextUrl}`;
  }
  const params = {};
  if (sectionId) {
    params.sectionId = sectionId;
  }
  // authorization is optional on this request
  const authHeader = getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  const config = {
    method: 'get',
    params: params,
    url: url,
    headers: headers,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Create a new comment/reply.
 *
 * POST /page/{pageId}/pref/{userId}/comments
 * POST /page/{pageId}/pref/{userId}/comments/{commentId}
 *
 * Parameters:
 * @payload.pageId: specified page id
 * @payload.userId: voter pref page user id
 * @payload.commentId: (Optional) commentId to create a reply to
 * @payload.text {String}: Some comment text
 * @payload.sectionId {String}: the section to associate this comment with
 */
export const createComment = (payload) => {
  const {pageId, userId, text, sectionId, commentId} = payload;
  // paginated responses return page.next, which indicates the next
  // full url to query to get the next page.
  let url = `${apiBase}/page/${pageId}/pref/${userId}/comments`;
  if (commentId) {
    url += `/${commentId}/replies`
  }
  const headers = {
    Authorization: getAuthHeader()
  };
  const data = {
    text: text,
  };
  // only comments, not replies, need section
  if (!commentId) {
    data.sectionId = sectionId;
  }
  const config = {
    method: 'post',
    url: url,
    headers: headers,
    data: data,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Delete a comment/reply.
 *
 * DELETE /page/{pageId}/pref/{userId}/comments/{commentId}
 * DELETE /page/{pageId}/pref/{userId}/comments/{commentId}/replies/{replyId}
 *
 * Parameters:
 * @payload.pageId: the candidate/prop page to write to
 * @payload.userId: the user's vote pref page
 * @payload.commentId: comment to delete, or (if replyId supplied) parent comment
 * @payload.replyId: (Optional) reply to delete
 */
export const deleteComment = (payload) => {
  const {pageId, userId, commentId, replyId} = payload
  let url = `${apiBase}/page/${pageId}/pref/${userId}/comments/${commentId}`;
  if (replyId) {
    url += `/replies/${replyId}`
  }
  const authHeader = getAuthHeader();
  const headers = {
    Authorization: authHeader,
  };
  const config = {
    method: 'delete',
    url: url,
    headers: headers,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Update an existing comment.
 *
 * POST /page/{pageId}/pref/{userId}/comments/{commentId}
 * POST /page/{pageId}/pref/{userId}/comments/{commentId}/replies/{replyId}
 *
 * Parameters:
 * @payload.pageId: specified page id
 * @payload.userId: voter pref page user id
 * @payload.commentId {String}: comment ID to update
 * @payload.replyId {String}: (Optional) comment ID to update
 * @payload.text {String}: Some comment text
 * @payload.sectionId {String}: the section to associate this comment with
 */
export const updateComment = (payload) => {
  const {pageId, userId, commentId, replyId, text, sectionId} = payload
  // paginated responses return page.next, which indicates the next
  // full url to query to get the next page.
  let url = `${apiBase}/page/${pageId}/pref/${userId}/comments/${commentId}`;
  if (replyId) {
    url += `/replies/${replyId}`;
  }
  const headers = {
    Authorization: getAuthHeader()
  };
  const data = {
    text: text,
    sectionId: sectionId,
  };
  const config = {
    method: 'post',
    url: url,
    headers: headers,
    data: data,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Upvote a comment.
 *
 * POST /page/{pageId}/pref/{userId}/comments/{commentId}/up
 * POST /page/{pageId}/pref/{userId}/comments/{commentId}/replies/{replyId}/up
 *
 * Parameters:
 * @payload.pageId: specified page id
 * @payload.userId: voter pref page user id
 * @payload.commentId {String}: comment ID to update (or parent if reply)
 * @payload.replyId {String}: (Optional) comment ID to update
 */
export const upvoteComment = (payload) => {
  const {pageId, userId, commentId, replyId } = payload
  let url = `${apiBase}/page/${pageId}/pref/${userId}/comments/${commentId}`;
  if (replyId) {
    url += `/replies/${replyId}/up`;
  }
  else {
    url += "/up";
  }
  const headers = {
    Authorization: getAuthHeader()
  };
  const config = {
    method: 'post',
    url: url,
    headers: headers,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};

/**
 * Get search query results.
 *
 * GET /search?q=search+query
 *
 * @query {string}: The search query terms
 */
export const search = (query) => {
  const url = `${apiBase}/search`;
  const config = {
    method: 'get',
    params: {
      q: query
    },
    url: url,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};


/**
 * Flag a page in the site
 *
 * POST /flag
 *
 * @payload.reason: Flag reason text
 * @payload.resource: Resource URL indicating flagged item
 * @payload.contactInfo: (Optional) contact email for flagger
 */
export const flag = (data) => {
  const url = `${apiBase}/flag`;
  // authorization is optional on this request
  const authHeader = getAuthHeader();
  const headers = {};
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  const config = {
    method: 'post',
    url: url,
    headers: headers,
    data: data,
  };
  return axios(config)
    .then((response) => response.data)
    .catch(handleError);
};




// WEBPACK FOOTER //
// ./src/api/requests.js